<script lang="ts">
  import { getContext } from 'svelte';
  import { page } from '$app/stores';
  import { slide } from 'svelte/transition';
  import { quintOut } from 'svelte/easing';
  import {
    MailAltIcon,
    PhoneAltFilledIcon,
    UserIcon,
  } from '../../components/icons';
  import { BookingDateTimePicker, BookingProgress } from '.';
  import { isValidEmail } from '../../helpers/validation-helper';
  import { AlertBanner, Button, Input, TextArea } from 'core-components';
  import { formatPhoneNumber } from 'core-components/helpers';
  import { BookingSent } from '.';
  import type {
    GlobalProperties,
    UseFormattedProps,
  } from 'customer-components/lib/types';

  type FormError = {
    field: string;
    message: string;
  };

  export let properties: UseFormattedProps;
  export let generalSettings: GlobalProperties['generalSettings'];
  export let sent = false;
  export let booking = false;
  export let externalBooking = false;
  export let hasCalendar = false;
  export let form: HTMLFormElement | null = null;

  const hash = getContext<string>('hash');

  let fullName = '';
  let email = '';
  let phone = '';
  let comments = '';
  let date = '';
  let time = '';

  let isLoading = false;
  let isContactPage = $page?.data.pageName === 'contact';
  let errors = [];
  let submitError = false;

  $: nameError = errors.find(x => x.field === 'fullName');
  $: emailError = errors.find(x => x.field === 'email');
  $: phoneError = errors.find(x => x.field === 'phone');

  export const handleSubmit = async (event: SubmitEvent) => {
    if (isLoading) return;
    isLoading = true;

    if (!isValidForm()) {
      isLoading = false;
      return;
    }

    if (externalBooking && generalSettings?.contacts?.externalUrl?.value) {
      // If value doesn't have https:// or http://, add https://
      if (!generalSettings.contacts.externalUrl.value?.includes('http')) {
        generalSettings.contacts.externalUrl.value = `https://${generalSettings.contacts.externalUrl.value}`;
      }
      window.open(generalSettings.contacts.externalUrl.value);
    }

    try {
      await properties.submitLead(event);
      sent = true;
      // clear form
      fullName = '';
      email = '';
      phone = '';
      comments = '';
      date = '';
      time = '';
    } catch (error) {
      submitError = true;
    }

    isLoading = false;
  };

  const handlePhoneChange = () => {
    phone = formatPhoneNumber(phone);
  };

  const isValidForm = () => {
    errors = [];
    submitError = false;

    if (fullName.trim().length === 0)
      errors.push({
        field: 'fullName',
        message: 'This field is missing. Please tell us your name.',
      });

    if (email.trim().length === 0)
      errors.push({
        field: 'email',
        message: 'This field is missing. Provide an email address.',
      });

    if (email.trim().length > 0 && !isValidEmail(email))
      errors.push({
        field: 'email',
        message: 'Please enter a valid email.',
      });

    if (phone.length > 0 && phone.length !== 14)
      errors.push({
        field: 'phone',
        message: 'Please enter a valid phone number.',
      });

    if (showDateTimePicker) {
      if (!date)
        errors.push({
          field: 'date',
          message: 'This field is missing. Select a date.',
        });

      if (!time)
        errors.push({
          field: 'time',
          message: 'This field is missing. Select a time.',
        });
    }

    errors = errors;
    return errors.length === 0;
  };

  let innerWidth = 0;
  $: isMobile = innerWidth <= 768;
  $: showDateTimePicker = hasCalendar && !isContactPage;
</script>

<svelte:window bind:innerWidth />

{#if !sent}
  <div class="card" transition:slide={{ duration: 300, easing: quintOut }}>
    <div class="card-content">
      {#if externalBooking}
        <BookingProgress />
      {/if}
      <form
        bind:this={form}
        class="contact-form"
        class:is-loading={isLoading}
        on:submit|preventDefault={handleSubmit}
        novalidate
      >
        {#if showDateTimePicker}
          <BookingDateTimePicker {errors} bind:date bind:time />
        {/if}
        <div class="row">
          <Input
            label="Name"
            name="fullName"
            placeholder="Name"
            bind:value={fullName}
            required
            disabled={isLoading}
            size="2xlarge"
            error={!!nameError}
            errorMessage={nameError?.message}
            extend
          >
            <UserIcon slot="icon" />
          </Input>

          <Input
            label="Email"
            name="email"
            placeholder="Email"
            bind:value={email}
            type="email"
            required
            disabled={isLoading}
            size="2xlarge"
            error={!!emailError}
            errorMessage={emailError?.message}
            extend
          >
            <MailAltIcon slot="icon" />
          </Input>
        </div>
        <Input
          label="Phone Number"
          rightLabel="Optional"
          name="phone"
          placeholder="e.g (000) 000-0000"
          bind:value={phone}
          type="tel"
          on:input={handlePhoneChange}
          disabled={isLoading}
          size="2xlarge"
          error={!!phoneError}
          errorMessage={phoneError?.message}
          extend
        >
          <PhoneAltFilledIcon slot="icon" />
        </Input>
        <TextArea
          label="Your Message"
          placeholder="Tell us more about what you need..."
          name="comments"
          bind:value={comments}
          rows={isMobile ? 5 : 9}
          disabled={isLoading}
          size="giant"
          maxLength={500}
          extend
        />
        <Button variant="priority" type="submit" disabled={isLoading} fullWidth>
          {#if externalBooking}
            Next
          {:else if booking}
            Book An Appointment
          {:else}
            Submit
          {/if}
        </Button>
        <input type="hidden" name="hash" value={hash} />
      </form>
      {#if submitError}
        <div
          class="alert"
          transition:slide={{ duration: 300, easing: quintOut }}
        >
          <AlertBanner
            title="Submission Error"
            message="Sorry, we're experiencing technical issues. Please try submitting the form again later."
            variant="destructive"
            isClosable={false}
          />
        </div>
      {/if}
    </div>
  </div>
{:else}
  <BookingSent
    bind:sent
    isSplit={true}
    phone={generalSettings?.contacts?.phone}
    isFromCTA={true}
  />
{/if}

<style lang="scss">
  .contact-form {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-mini);
    transition: all 0.2s ease;
  }

  .contact-form.is-loading {
    opacity: 0.75;
  }

  .contact-form.is-loading * {
    pointer-events: none;
  }

  .row {
    display: flex;
    gap: var(--spacing-mini);
  }

  .card {
    width: 100%;
    background: #fff;
    border: 1px solid #ebedef;
    border-radius: var(--spacing-small-xl);
    box-shadow:
      0px 4px 6px -2px rgba(16, 24, 40, 0.03),
      0px 12px 16px -4px rgba(16, 24, 40, 0.08);
    box-sizing: border-box;
  }

  .card-content {
    padding: var(--spacing-large);
  }

  .alert {
    margin-top: var(--spacing-small);
    text-align: left;
  }

  @media screen and (max-width: 767px) {
    .card-content {
      padding: var(--spacing-mini);
    }

    .row {
      flex-direction: column;
    }
  }
</style>
